import React, { lazy } from 'react';
import { Debts } from '@datanomik/core-ui/icons';

const LiabilityWriteGuard = lazy(() =>
  import('./shared/components/LiabilityWriteGuard').then((m) => ({ default: m.LiabilityWriteGuard })),
);

// "Visão geral": the rebuilt debts home (ex "Visão geral 2"), the module's index route. The older
// unified page it replaced was deleted with the 2026-08-20 gap audit; the insights engine it used
// (debtInsightsEngine + its drawer/modal) stays on disk on purpose — insights come back later.
// Same module shell/tab bar — it is a tab, not a separate screen.
const DebtsOverviewV2Page = lazy(() =>
  import('./overview-v2/DebtsOverviewV2Page').then((m) => ({ default: m.DebtsOverviewV2Page })),
);

// Back-compat for the retired `/debts/overview-2` address: preserves the query string so
// lens/pivot deep-links (`?view=escada`, `?pivot=owner`) still land on the same view instead of
// resetting to the default.
const OverviewV2Redirect = lazy(() =>
  import('./overview-v2/OverviewV2Redirect').then((m) => ({ default: m.OverviewV2Redirect })),
);

const MyDebtsPage = lazy(() =>
  import('./my-debts/MyDebtsPage').then((m) => ({ default: m.MyDebtsPage })),
);

const DebtSchedulePage = lazy(() =>
  import('./debt-schedule/DebtSchedulePage').then((m) => ({ default: m.DebtSchedulePage })),
);

const NewDebtPage = lazy(() =>
  import('./new-debt/NewDebtPage').then((m) => ({ default: m.NewDebtPage })),
);

const MutuosListPage = lazy(() =>
  import('./mutuos/MutuosListPage').then((m) => ({ default: m.MutuosListPage })),
);

const NewMutuoPage = lazy(() =>
  import('./mutuos/NewMutuoPage').then((m) => ({ default: m.NewMutuoPage })),
);

const MutuoDetailPage = lazy(() =>
  import('./mutuos/MutuoDetailPage').then((m) => ({ default: m.MutuoDetailPage })),
);

const CompaniesPage = lazy(() =>
  import('./mutuos/CompaniesPage').then((m) => ({ default: m.CompaniesPage })),
);

const SecuritizationsListPage = lazy(() =>
  import('./securitizations/SecuritizationsListPage').then((m) => ({
    default: m.SecuritizationsListPage,
  })),
);

const NewSecuritizationPage = lazy(() =>
  import('./securitizations/NewSecuritizationPage').then((m) => ({
    default: m.NewSecuritizationPage,
  })),
);

const SecuritizationDetailPage = lazy(() =>
  import('./securitizations/SecuritizationDetailPage').then((m) => ({
    default: m.SecuritizationDetailPage,
  })),
);

const CovenantsOverviewPage = lazy(() =>
  import('./covenants/CovenantsOverviewPage').then((m) => ({
    default: m.CovenantsOverviewPage,
  })),
);

// Debêntures tab (DNKP-3/T2): own routes mirroring the securitizações family (list/new/:id/edit),
// no new backend entity — same debt.debtType=DEBENTURE motor as Bancárias (D-DEB-6).
const DebenturesListPage = lazy(() =>
  import('./debentures/DebenturesListPage').then((m) => ({ default: m.DebenturesListPage })),
);

const NewDebenturePage = lazy(() =>
  import('./debentures/NewDebenturePage').then((m) => ({ default: m.NewDebenturePage })),
);

const DebentureDetailPage = lazy(() =>
  import('./debentures/DebentureDetailPage').then((m) => ({ default: m.DebentureDetailPage })),
);

// SWAP / NDF básico (DIVIDAS_SWAP_NDF_DESIGN_2026-07-31): "Dívidas -> Coberturas".
const HedgesListPage = lazy(() =>
  import('./hedges/HedgesListPage').then((m) => ({ default: m.HedgesListPage })),
);

const NewHedgePage = lazy(() =>
  import('./hedges/NewHedgePage').then((m) => ({ default: m.NewHedgePage })),
);

// hedge-list-link-detail follow-up: the detail screen (instrument + event history + void) — until
// now `hedges` (list) and `hedges/new` were the only two routes.
const HedgeDetailPage = lazy(() =>
  import('./hedges/HedgeDetailPage').then((m) => ({ default: m.HedgeDetailPage })),
);

const LiabilityClosingsPage = lazy(() =>
  import('./liability-closings/LiabilityClosingsPage').then((m) => ({ default: m.LiabilityClosingsPage })),
);

const MonthlyMovementsPage = lazy(() =>
  import('./monthly-movements/MonthlyMovementsPage').then((m) => ({ default: m.MonthlyMovementsPage })),
);

const CostByIndexerPreviewPage = lazy(() =>
  import('./cost-by-indexer/CostByIndexerPreviewPage').then((m) => ({ default: m.CostByIndexerPreviewPage })),
);

const MODULE_LABEL = 'Dívidas';
const MODULE_DESCRIPTION = 'Gestão de dívidas bancárias e intercompany.';

export const getDebtsRoutes = () => [
  {
    path: '',
    key: 'debts',
    label: MODULE_LABEL,
    description: MODULE_DESCRIPTION,
    element: <DebtsOverviewV2Page />,
    icon: <Debts />,
  },
  {
    path: 'overview-2',
    key: 'debts-overview-2',
    label: MODULE_LABEL,
    element: <OverviewV2Redirect />,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'new-debt',
    key: 'debts-new-debt',
    label: 'Nova dívida',
    element: <LiabilityWriteGuard action="create"><NewDebtPage /></LiabilityWriteGuard>,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'my-debts',
    key: 'debts-my-debts',
    label: MODULE_LABEL,
    description: MODULE_DESCRIPTION,
    element: <MyDebtsPage />,
    icon: <Debts />,
  },
  {
    path: 'my-debts/:id/schedule',
    key: 'debts-schedule',
    label: 'Cronograma',
    element: <DebtSchedulePage />,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'mutuos',
    key: 'debts-mutuos',
    label: MODULE_LABEL,
    description: MODULE_DESCRIPTION,
    element: <MutuosListPage />,
    icon: <Debts />,
  },
  {
    path: 'mutuos/new',
    key: 'debts-mutuos-new',
    label: 'Novo mútuo',
    element: <LiabilityWriteGuard action="create"><NewMutuoPage /></LiabilityWriteGuard>,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'mutuos/:id/edit',
    key: 'debts-mutuos-edit',
    label: 'Editar mútuo',
    element: <LiabilityWriteGuard action="edit"><NewMutuoPage /></LiabilityWriteGuard>,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'mutuos/:id',
    key: 'debts-mutuos-detail',
    label: 'Detalhe do mútuo',
    element: <MutuoDetailPage />,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'securitizations',
    key: 'debts-securitizations',
    label: MODULE_LABEL,
    description: MODULE_DESCRIPTION,
    element: <SecuritizationsListPage />,
    icon: <Debts />,
  },
  {
    path: 'securitizations/new',
    key: 'debts-securitizations-new',
    label: 'Nova securitização',
    element: <LiabilityWriteGuard action="create"><NewSecuritizationPage /></LiabilityWriteGuard>,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'securitizations/:id/edit',
    key: 'debts-securitizations-edit',
    label: 'Editar securitização',
    element: <LiabilityWriteGuard action="edit"><NewSecuritizationPage /></LiabilityWriteGuard>,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'securitizations/:id',
    key: 'debts-securitizations-detail',
    label: 'Detalhe da securitização',
    element: <SecuritizationDetailPage />,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'debentures',
    key: 'debts-debentures',
    label: MODULE_LABEL,
    description: MODULE_DESCRIPTION,
    element: <DebenturesListPage />,
    icon: <Debts />,
  },
  {
    path: 'debentures/new',
    key: 'debts-debentures-new',
    label: 'Nova debênture',
    element: <LiabilityWriteGuard action="create"><NewDebenturePage /></LiabilityWriteGuard>,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'debentures/:id/edit',
    key: 'debts-debentures-edit',
    label: 'Editar debênture',
    element: <LiabilityWriteGuard action="edit"><NewDebenturePage /></LiabilityWriteGuard>,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'debentures/:id',
    key: 'debts-debentures-detail',
    label: 'Detalhe da debênture',
    element: <DebentureDetailPage />,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'companies',
    key: 'debts-companies',
    label: 'Empresas',
    element: <CompaniesPage />,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'covenants',
    key: 'debts-covenants',
    label: MODULE_LABEL,
    description: MODULE_DESCRIPTION,
    element: <CovenantsOverviewPage />,
    icon: <Debts />,
  },
  {
    path: 'hedges',
    // Every tab of the unified shell reports the module's own label/description, never its tab
    // name (the tab bar already says "Coberturas"). This route used to call t() here instead —
    // module scope, evaluated on import before i18next finishes initializing, so the app bar
    // rendered an empty title and the whole bar shifted when landing on /debts/hedges.
    key: 'debts-hedges',
    label: MODULE_LABEL,
    description: MODULE_DESCRIPTION,
    element: <HedgesListPage />,
    icon: <Debts />,
  },
  {
    path: 'hedges/new',
    key: 'debts-hedges-new',
    label: 'Nova cobertura',
    element: <LiabilityWriteGuard action="create"><NewHedgePage /></LiabilityWriteGuard>,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'hedges/:id',
    key: 'debts-hedges-detail',
    label: 'Detalhe da cobertura',
    element: <HedgeDetailPage />,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
  {
    path: 'closings',
    key: 'debts-closings',
    label: MODULE_LABEL,
    description: MODULE_DESCRIPTION,
    element: <LiabilityClosingsPage />,
    icon: <Debts />,
  },
  {
    path: 'reports/monthly-movements',
    key: 'debts-reports-monthly-movements',
    label: MODULE_LABEL,
    description: MODULE_DESCRIPTION,
    element: <MonthlyMovementsPage />,
    icon: <Debts />,
  },
  {
    path: 'reports/cost-by-indexer-preview',
    key: 'debts-cost-by-indexer-preview',
    label: 'Custo por indexador (preview)',
    element: <CostByIndexerPreviewPage />,
    showInMenu: false,
    hiddenPageTitle: true,
    hideBackButton: true,
  },
];
